import { gql, useQuery } from '@apollo/client';
import { ApiErrorCode } from '@orn/global/errors/apiErrorCodes.js';
import Button from '@orn/shared-web/components/Button/Button';
import LoadingIndicator from '@orn/shared-web/components/LoadingIndicator';
import Modal from '@orn/shared-web/components/Modal';
import { ChevronRightIcon, PlusIcon } from '@orn/shared-web/icons';
import * as React from 'react';

import Entity from '../../entity';
import EntityAvatar from '../../entity/EntityAvatar';

import type {
  GetBankConnectionsQuery,
  GetBankConnectionsQueryVariables,
} from './__generated__/PlaidConnectionModal.gql';

export const GET_BANK_CONNECTIONS = gql`
  query GetBankConnections {
    getBankConnectionsForUser {
      id
      bank {
        id
        ...EntityAvatar
      }
    }
  }
  ${EntityAvatar.Fragment}
`;

export type BankConnection = GetBankConnectionsQuery['getBankConnectionsForUser'][number];

const getErrorMessage = (errorCode: ApiErrorCode | null | undefined) => {
  if (!errorCode) {
    return null;
  }

  switch (errorCode) {
    case ApiErrorCode.DUPLICATE_ITEM:
      return 'You already have a connection with this institution. Please select it from the list below.';
    case ApiErrorCode.PLAID_LINK_TOKEN_FAILED:
    case ApiErrorCode.PLAID_EXCHANGE_FAILED:
    default:
      return 'An error occurred while connecting your account. Please try again or contact support if the issue persists.';
  }
};

interface IBankConnectionRowProps {
  bankConnection: BankConnection;
  onClick: () => void;
}

const BankConnectionRow = ({ bankConnection, onClick }: IBankConnectionRowProps) => {
  return (
    <Button
      onClick={onClick}
      color="tertiary"
      className="*:data-text:min-w-0 h-[72px] w-full justify-start gap-3 rounded-xl py-4 pl-4 pr-3"
      iconLeading={<Entity.Avatar entity={bankConnection.bank} size="md" />}
      iconTrailing={<ChevronRightIcon className="text-quaternary ml-auto size-5 shrink-0" />}
      noTextPadding
    >
      <span className="text-primary block truncate text-sm font-medium">{bankConnection.bank.name}</span>
    </Button>
  );
};

interface IBasePlaidConnectionModalProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  bankConnections: BankConnection[];
  isLoading?: boolean;
  /** Called when user selects a bank connection or adds new. bankConnectionId is undefined for new institution. */
  onSelect: (bankConnectionId: string | undefined) => void;
  errorMessage?: string | null;
}

export const BasePlaidConnectionModal = ({
  isOpen,
  onOpenChange,
  bankConnections,
  isLoading = false,
  onSelect,
  errorMessage,
}: IBasePlaidConnectionModalProps) => {
  const handleClose = React.useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  return (
    <Modal isOpen={isOpen} onOpenChange={onOpenChange} size="sm">
      <Modal.Header onClose={handleClose}>
        <div className="flex flex-col gap-1.5">
          <span className="text-primary text-lg font-semibold">Add account</span>
          <span className="text-quaternary text-sm font-normal">
            To add another account, select a financial institution you've already connected using Plaid. Or connect a
            new financial institution.
          </span>
        </div>
      </Modal.Header>
      <Modal.Body className="pt-0">
        {errorMessage && (
          <div className="bg-error-secondary text-error-primary mb-3 rounded-lg px-3 py-2 text-sm">{errorMessage}</div>
        )}
        {isLoading ? (
          <div className="flex h-[72px] items-center justify-center">
            <LoadingIndicator />
          </div>
        ) : (
          <div className="flex w-full flex-col gap-0.5">
            {bankConnections.map((bankConnection) => (
              <BankConnectionRow
                key={bankConnection.id}
                bankConnection={bankConnection}
                onClick={() => onSelect(bankConnection.id)}
              />
            ))}
          </div>
        )}
        <div className="mt-2 flex w-full">
          <Button
            onClick={() => onSelect(undefined)}
            className="flex w-full justify-center"
            color="tertiary"
            iconLeading={<PlusIcon />}
            size="sm"
          >
            Connect financial institution
          </Button>
        </div>
      </Modal.Body>
    </Modal>
  );
};

interface IPlaidConnectionModalProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  /** Called when user selects a bank connection or adds new. bankConnectionId is undefined for new institution. */
  onSelect: (bankConnectionId: string | undefined) => void;
  errorCode?: ApiErrorCode | null;
}

const PlaidConnectionModal = ({ isOpen, onOpenChange, onSelect, errorCode }: IPlaidConnectionModalProps) => {
  const { data, loading } = useQuery<GetBankConnectionsQuery, GetBankConnectionsQueryVariables>(GET_BANK_CONNECTIONS, {
    skip: !isOpen,
  });

  // const bankConnections = React.useMemo(() => data?.getBankConnectionsForUser ?? [], [data]);
  // Cache the last valid data so it persists during the modal close animation
  const cachedConnectionsRef = React.useRef<BankConnection[]>([]);
  if (data?.getBankConnectionsForUser) {
    cachedConnectionsRef.current = data.getBankConnectionsForUser;
  }
  const bankConnections = cachedConnectionsRef.current;

  const handleSelect = React.useCallback(
    (bankConnectionId: string | undefined) => {
      onOpenChange(false);
      onSelect(bankConnectionId);
    },
    [onOpenChange, onSelect]
  );

  return (
    <BasePlaidConnectionModal
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      bankConnections={bankConnections}
      isLoading={loading}
      onSelect={handleSelect}
      errorMessage={getErrorMessage(errorCode)}
    />
  );
};

export default PlaidConnectionModal;
